import { StateFrom } from 'xstate';
import {
  Locale,
  Order,
  PaymentTypes,
  APPLICATION_STATUS,
  MerchantPaymentProduct,
} from '@ads-bread/shared/bread/codecs';
import {
  APPLICATION_EXPERIENCE_CODES,
  APPLICATION_STATUS_CODES,
  NAMESPACE_MAP,
  NamespacePath,
} from '@ads-bread/shared/bread/util';
import {
  isApproved,
  needsFullIIN,
  needsIDVerificationAlloy,
} from '../../lib/applicationStatus';
import { authenticationMachine } from '../AuthenticationMachineContext/authenticationMachine';
import { BuyerMachineSnapshot } from '../BuyerMachineContext/useBuyerMachine';
import { ApplicationContext } from './applicationMachine';
import {
  ApplicationCheckoutResult,
  ApplicationPrepareCheckoutResult,
  CreateApplicationResult,
  EvaluateApplicationErrorResult,
  FetchLatestApplicationResult,
  ReCreateApplicationResult,
  ReCreateInStoreContingentApplicationResult,
} from './types/actors';
import {
  ApplicationMachineEvents,
  SendAssignSelectedOfferParams,
} from './types/events';
import {
  hasExpiredCloverShortCode,
  isIneligibleAddress as isIneligibleAddressUtil,
  isUnexpiredInStoreApplication as isUnexpiredInStoreApplicationUtil,
  isValidApplication,
  matchesPreviouslyApproved,
} from './utils';

type ApplicationGuardArgs = {
  context: ApplicationContext;
  event: ApplicationMachineEvents;
};

type AuthenticationMachineSnapshot = StateFrom<typeof authenticationMachine>;

/**
 * Returns true when the selected offer is an installments offer.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isInstallmentOfferSelected = (
  _: ApplicationGuardArgs,
  params: SendAssignSelectedOfferParams
): boolean => {
  return params.selectedOffer.paymentProduct.type === PaymentTypes.INSTALLMENTS;
};

/**
 * Returns true when the selected offer is a split pay offer.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isSplitPayOfferSelected = (
  _: ApplicationGuardArgs,
  params: SendAssignSelectedOfferParams
): boolean => {
  return params.selectedOffer.paymentProduct.type === PaymentTypes.SPLITPAY;
};

/**
 * Returns true when the selected offer is a hybrid line of credit offer.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isHybridLineOfCreditOfferSelected = (
  _: ApplicationGuardArgs,
  params: SendAssignSelectedOfferParams
): boolean => {
  return (
    params.selectedOffer.paymentProduct.type ===
    PaymentTypes.HYBRID_LINE_OF_CREDIT
  );
};

/**
 * Returns true when the application result has an ineligible address.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isIneligibleAddress = (
  _: ApplicationGuardArgs,
  params: FetchLatestApplicationResult
): boolean => {
  const app = params.applicationRes.result;
  if (!app) {
    return false;
  }
  return isIneligibleAddressUtil(app);
};

/**
 * Returns true when the application result requires full IIN entry.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const guardNeedsFullIIN = (
  _: ApplicationGuardArgs,
  params:
    | FetchLatestApplicationResult
    | CreateApplicationResult
    | ReCreateApplicationResult
): boolean => {
  const app = params.applicationRes.result;
  if (!app) {
    return false;
  }
  return needsFullIIN(app);
};

/**
 * Returns true when the application result is in an approved state.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const guardIsApproved = (
  _: ApplicationGuardArgs,
  params:
    | CreateApplicationResult
    | ReCreateApplicationResult
    | ReCreateInStoreContingentApplicationResult
): boolean => {
  const application = params.applicationRes.result;
  if (!application) {
    return false;
  }
  return isApproved(application);
};

/**
 * Returns true when application checkout has completed.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isCheckoutCompleted = (
  _: ApplicationGuardArgs,
  params: ApplicationCheckoutResult
): boolean => {
  const status = params.applicationRes.result?.status;
  return status === APPLICATION_STATUS.CHECKOUT_COMPLETED;
};

/**
 * Returns true when checkout returned a down-payment authorization decline.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isDownPaymentAuthDeclinedError = (
  _: ApplicationGuardArgs,
  params: ApplicationCheckoutResult
): boolean => {
  const app = params.applicationRes.result;
  if (!app) {
    return false;
  }
  return !!app.statusCodes?.some(
    (code) =>
      code ===
      APPLICATION_STATUS_CODES.DownPaymentAuthDeclinedDeclinedRetryPaymentMethod
  );
};

/**
 * Returns true when application checkout has been prepared.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isCheckoutPrepared = (
  _: ApplicationGuardArgs,
  params: ApplicationPrepareCheckoutResult
): boolean => {
  const status = params.applicationRes.result?.status;
  return status === APPLICATION_STATUS.CHECKOUT_PREPARED;
};

/**
 * Returns true when the application prepare-checkout response carried an error.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isErrorResult = (
  _: ApplicationGuardArgs,
  params: ApplicationPrepareCheckoutResult
): boolean => {
  const error = params.applicationRes.error;
  return !!(error?.reason || error?.message);
};

/**
 * Returns true when the error matches the PO box ineligible bucket.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isPoBoxAddressIneligible = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.PO_BOX_INELIGIBLE.includes(
      experienceCode
    );
  }
  return (
    statusCode ===
    APPLICATION_STATUS_CODES.EligibilityIneligibleInvalidAddressPOBox
  );
};

/**
 * Returns true when the error matches the invalid-address no-match bucket.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isInvalidAddressNoMatchIneligible = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.INVALID_ADDRESS_NO_MATCH_INELIGIBLE.includes(
      experienceCode
    );
  }
  return (
    statusCode ===
    APPLICATION_STATUS_CODES.EligibilityIneligibleInvalidAddressNoMatch
  );
};

/**
 * Returns true when the error matches the buyer-hash failed bucket.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isBuyerHashFailed = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.BUYER_HASH_FAILED.includes(
      experienceCode
    );
  }
  return (
    statusCode ===
    APPLICATION_STATUS_CODES.ApplicationCancelledBuyerHashCheckFailed
  );
};

/**
 * Returns true when the error matches a credit-freeze bucket. Credit Freeze
 * currently spans experiences '005' and '006' until the new screens land in
 * APEX-7891.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isCreditFreeze = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.CREDIT_FREEZE.includes(experienceCode);
  }
  return [
    APPLICATION_STATUS_CODES.DecisionIncompleteIncompleteCreditFreeze,
    APPLICATION_STATUS_CODES.KYCIncompleteCreditFreeze,
    APPLICATION_STATUS_CODES.FraudIncompleteCreditFreeze,
  ].includes(statusCode);
};

/**
 * Returns true when the error indicates a KYC denial.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isKycDenial = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return [
    APPLICATION_STATUS_CODES.DecisionDeniedInsufficientData,
    APPLICATION_STATUS_CODES.KYCFailed,
  ].includes(statusCode);
};

/**
 * Returns true when the error indicates a previous-denial ineligibility.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isPreviousDenialIneligible = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return (
    statusCode === APPLICATION_STATUS_CODES.DecisionIneligiblePreviousDenial
  );
};

/**
 * Returns true when the error indicates max-card attempts ineligibility.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isMaxCardIneligible = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return (
    statusCode === APPLICATION_STATUS_CODES.DecisionIneligibleMaxCardAttempt
  );
};

/**
 * Returns true when the error indicates an outstanding-loans ineligibility.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isOutstandingLoansIneligible = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return (
    statusCode === APPLICATION_STATUS_CODES.DecisionIneligibleOutstandingLoans
  );
};

/**
 * Returns true when the error indicates buyer-age ineligibility.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isAgeIneligible = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return statusCode === APPLICATION_STATUS_CODES.EligibilityIneligibleBuyerAge;
};

/**
 * Returns true when the error matches Alabama-military age bucket.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isAgeAlabamaMilitary = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.AGE_ALABAMA_MILITARY.includes(
      experienceCode
    );
  }
  return (
    statusCode === APPLICATION_STATUS_CODES.EligibilityIneligibleBuyerAgeAL
  );
};

/**
 * Returns true when the error indicates buyer-status ineligibility.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isBuyerStatusIneligible = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return (
    statusCode === APPLICATION_STATUS_CODES.EligibilityIneligibleBuyerStatus
  );
};

/**
 * Returns true when the error indicates the buyer skipped installments.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isBuyerSkippedInstallments = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return (
    statusCode ===
    APPLICATION_STATUS_CODES.DecisionIneligibleIneligibleSkippedInstallments
  );
};

/**
 * Returns true when the error indicates a location-based ineligibility.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isLocationIneligible = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return [
    APPLICATION_STATUS_CODES.EligibilityIneligibleBuyerRegion,
    APPLICATION_STATUS_CODES.EligibilityIneligibleBuyerCountry,
  ].includes(statusCode);
};

/**
 * Returns true when the error indicates a needs-action warning.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isNeedsActionWarning = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return [
    APPLICATION_STATUS_CODES.FraudIncompleteExtendedFraudAlert,
    APPLICATION_STATUS_CODES.FraudIncompleteHighRisk,
    APPLICATION_STATUS_CODES.FraudIncompleteLowRisk,
    APPLICATION_STATUS_CODES.KYCIncompleteFailed,
  ].includes(statusCode);
};

/**
 * Returns true when the error indicates a needs-additional-information warning.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isNeedsAdditionalInformationWarning = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.NEEDS_ADDITIONAL_INFORMATION_WARNING.includes(
      experienceCode
    );
  }
  return [
    APPLICATION_STATUS_CODES.FraudIncompleteVictimStatementNoSafeHarbor,
    APPLICATION_STATUS_CODES.DecisionIncompleteNoEFANumber,
  ].includes(statusCode);
};

/**
 * Returns true when the error indicates a fraud denial.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isFraudDenial = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return statusCode === APPLICATION_STATUS_CODES.FraudFailed;
};

/**
 * Returns true when the error matches a credit-denial bucket. This captures
 * both experience codes '008' and '009' until the new screen ships in
 * APEX-7891.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isCreditDenial = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.CREDIT_DENIAL.includes(experienceCode);
  }
  return [
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedFraudChargedOff,
    APPLICATION_STATUS_CODES.DecisionDeniedDeceasedChargedOff,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedCredit,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedNoCreditFile,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedNoCreditScore,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedThinFile,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedRepossessions,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedForeclosure,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedDerogatoryRecord,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedTradePastDue,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedCollections,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedRevolvingCreditUtilization,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedInsufficientCapacity,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedAuthorizedTradelines,
    APPLICATION_STATUS_CODES.DecisionDeniedLowCreditLimit,
    APPLICATION_STATUS_CODES.DecisionDeniedLowFicoLowCreditLimit,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedLowFICO,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedCrust,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedBreadChargeOff,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedPastLoanOverdue,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedLoanCurrentlyOverdue,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedChargeOff,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedAccountPastDue,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedInquiries,
    APPLICATION_STATUS_CODES.DownPaymentAuthDeclined,
    APPLICATION_STATUS_CODES.DecisionDeniedDenied,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedCreditAbuse,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedCreditopticsDeceased,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedCreditopticsDecline,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedBureauDeceased,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedPreciseidDeceased,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedPreciseidDecline,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedEquifaxDeceased,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedBureauData,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedLowBureau,
    APPLICATION_STATUS_CODES.FraudDeniedPreciseIDDenied,
    APPLICATION_STATUS_CODES.FraudDeniedDeceased,
    APPLICATION_STATUS_CODES.DecisionIneligibleIneligibleCart,
    APPLICATION_STATUS_CODES.DecisionIneligibleMaxBureauAttempts,
    APPLICATION_STATUS_CODES.DecisionIncompleteNoEFANumber,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedRevolvingDebtTooHigh,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedRevolvingDebtTooLow,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedSelfReportedIncome,
    APPLICATION_STATUS_CODES.DecisionDeniedDeniedLowVantage,
    APPLICATION_STATUS_CODES.DecisionDeniedLowCrustLowScore,
    APPLICATION_STATUS_CODES.FraudDenied,
    APPLICATION_STATUS_CODES.FraudDeniedFraudFail,
    APPLICATION_STATUS_CODES.FraudDeniedIDDenial,
    APPLICATION_STATUS_CODES.FraudDeniedManualDenial,
    APPLICATION_STATUS_CODES.FraudDeniedBlockListDenial,
  ].includes(statusCode);
};

/**
 * Returns true when the error matches the sanctions OFAC denial bucket.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isSanctionsOFACDenial = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.SANCTIONS_OFAC_DENIAL.includes(
      experienceCode
    );
  }
  return statusCode === APPLICATION_STATUS_CODES.SanctionsIncompleteFailed;
};

/**
 * Returns true when the error matches the sanctions denial bucket. Currently
 * only used by RBC.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isSanctionsDenial = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return statusCode === APPLICATION_STATUS_CODES.SanctionsFailed;
};

/**
 * Returns true when the error indicates a fraud-alert denial.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isFraudAlertDenial = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return false;
  }
  return statusCode === APPLICATION_STATUS_CODES.DecisionDeniedDeniedFraudAlert;
};

/**
 * Returns true when the error indicates a capacity-recheck bucket.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isCapacityRecheck = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.CAPACITY_RECHECK.includes(
      experienceCode
    );
  }
  return (
    statusCode === APPLICATION_STATUS_CODES.CapacityCancelledContingent ||
    statusCode === APPLICATION_STATUS_CODES.CapacityCancelledPartial
  );
};

/**
 * Returns true when the error indicates an Alloy fraud-system outage.
 * @param _ (context, event)
 * @param params
 * @returns boolean
 */
export const isAlloySystemOutage = (
  _: ApplicationGuardArgs,
  params: EvaluateApplicationErrorResult
): boolean => {
  const { statusCode, experienceCode } = params;
  if (experienceCode) {
    return APPLICATION_EXPERIENCE_CODES.ALLOY_SYSTEM_OUTAGE.includes(
      experienceCode
    );
  }
  return statusCode === APPLICATION_STATUS_CODES.FraudIncompleteFraudOutage;
};

/**
 * Returns true once every required child machine has reported ready.
 * @param args ApplicationGuardArgs
 * @returns boolean
 */
export const requiredChildMachinesReady = ({
  context: ctx,
}: ApplicationGuardArgs): boolean => {
  return ctx.childMachinesRequired.every((machineID) =>
    ctx.childMachinesReady.has(machineID)
  );
};

type IsUnexpiredInStoreApplicationDeps = {
  namespace: NamespacePath;
  isVirtualCard: boolean;
};

/**
 * Factory for `isUnexpiredInStoreApplication`. Requires the active namespace
 * and the current virtual-card flag.
 * @param deps
 * @returns guard that returns true when the in-store application has not expired
 */
export const createIsUnexpiredInStoreApplication =
  (deps: IsUnexpiredInStoreApplicationDeps) =>
  (_: ApplicationGuardArgs, params: FetchLatestApplicationResult): boolean => {
    const app = params.applicationRes.result;
    if (!app) {
      return false;
    }
    return isUnexpiredInStoreApplicationUtil(
      deps.namespace,
      app,
      deps.isVirtualCard
    );
  };

type IsInvalidApplicationDeps = {
  namespace: NamespacePath;
  contextBuyerID: string | null;
  merchantPaymentProducts: MerchantPaymentProduct[];
  isVirtualCard: boolean;
};

/**
 * Factory for `isInvalidApplication`. Branches on namespace and falls back to
 * the standard application validation; in-store applies the expired Clover
 * short-code check as well.
 * @param deps
 * @returns guard that returns true when the application is considered invalid
 */
export const createIsInvalidApplication =
  (deps: IsInvalidApplicationDeps) =>
  (_: ApplicationGuardArgs, params: FetchLatestApplicationResult): boolean => {
    // Special case for RBC because we should fast forward to terms
    if (deps.namespace === 'rbc') {
      return false;
    }
    const app = params.applicationRes.result;
    if (!app) {
      return true;
    }

    // Special handling for in store clover expired short codes
    if (deps.namespace === NAMESPACE_MAP.InStore) {
      const isExchangedBuyer = !!deps.contextBuyerID;
      return (
        !isValidApplication(app, deps.merchantPaymentProducts) ||
        hasExpiredCloverShortCode(app, deps.isVirtualCard, isExchangedBuyer)
      );
    }

    return !isValidApplication(app, deps.merchantPaymentProducts);
  };

type IsCompleteMismatchedBuyerDeps = {
  authState: AuthenticationMachineSnapshot;
  buyerState: BuyerMachineSnapshot;
};

/**
 * Factory for `isCompleteMismatchedBuyer`. Requires both the authentication
 * and buyer machine snapshots.
 * @param deps
 * @returns guard that returns true when the buyer is complete but mismatched
 */
export const createIsCompleteMismatchedBuyer =
  (deps: IsCompleteMismatchedBuyerDeps) => (): boolean => {
    return (
      (deps.authState.matches({
        authenticated: { anonymous: 'mismatchedBuyerPII' },
      }) ||
        deps.authState.matches({
          authenticated: { complete: 'mismatchedBuyerPII' },
        })) &&
      deps.buyerState.matches({ ready: 'complete' })
    );
  };

type IsValidApplicationAndMatchesPreviouslyApprovedOrderDeps = {
  namespace: NamespacePath;
  contextBuyerID: string | null;
  merchantPaymentProducts: MerchantPaymentProduct[];
  isVirtualCard: boolean;
  order: Order;
  locale: Locale;
};

/**
 * Factory for `isValidApplicationAndMatchesPreviouslyApprovedOrder`. In-store
 * applications skip the order match and only verify validity (plus the Clover
 * short-code check).
 * @param deps
 * @returns guard that returns true when the application is valid and matches the prior order
 */
export const createIsValidApplicationAndMatchesPreviouslyApprovedOrder =
  (deps: IsValidApplicationAndMatchesPreviouslyApprovedOrderDeps) =>
  (_: ApplicationGuardArgs, params: FetchLatestApplicationResult): boolean => {
    const app = params.applicationRes.result;
    if (!app) {
      return false;
    }

    // In store applications are not compared to the order
    if (deps.namespace === NAMESPACE_MAP.InStore) {
      const isExchangedBuyer = !!deps.contextBuyerID;
      return (
        isValidApplication(app, deps.merchantPaymentProducts) &&
        !hasExpiredCloverShortCode(app, deps.isVirtualCard, isExchangedBuyer)
      );
    }

    return (
      matchesPreviouslyApproved(app, deps.order, deps.locale) &&
      isValidApplication(app, deps.merchantPaymentProducts)
    );
  };

type HasExchangedPaymentAgreementIdDeps = {
  paymentAgreementID: string | undefined;
  contextBuyerID: string | null;
};

/**
 * Factory for `hasExchangedPaymentAgreementId`. Returns true when both an
 * exchanged payment-agreement ID and a context buyer ID are present.
 * @param deps
 * @returns guard
 */
export const createHasExchangedPaymentAgreementId =
  (deps: HasExchangedPaymentAgreementIdDeps) => (): boolean => {
    return !!deps.paymentAgreementID && !!deps.contextBuyerID;
  };

type IsBuyerReadyAndCompleteDeps = {
  buyerState: BuyerMachineSnapshot;
};

/**
 * Factory for `isBuyerReadyAndComplete`. Returns true when the buyer machine
 * has reached the `ready.complete` state.
 * @param deps
 * @returns guard
 */
export const createIsBuyerReadyAndComplete =
  (deps: IsBuyerReadyAndCompleteDeps) => (): boolean => {
    return deps.buyerState.matches({ ready: 'complete' });
  };

type IsApprovedContingentInStoreDeps = {
  namespace: NamespacePath;
};

/**
 * Factory for `isApprovedContingentInStore`. Returns true when the namespace
 * is in-store and the application result carries the approved-contingent
 * status code.
 * @param deps
 * @returns guard
 */
export const createIsApprovedContingentInStore =
  (deps: IsApprovedContingentInStoreDeps) =>
  (
    _: ApplicationGuardArgs,
    params: CreateApplicationResult | FetchLatestApplicationResult
  ): boolean => {
    const application = params.applicationRes.result;
    if (!application) {
      return false;
    }

    const isApprovedContingent = application.statusCodes?.some(
      (statusCode) =>
        statusCode ===
        APPLICATION_STATUS_CODES.DecisionApprovedApprovedContingent
    );

    return !!(deps.namespace === NAMESPACE_MAP.InStore && isApprovedContingent);
  };

type NeedsIDVerificationAlloyDeps = {
  enableIDVerification: boolean;
};

/**
 * Factory for `needsIDVerificationAlloy`. Requires the merchant-level toggle
 * for ID verification.
 * @param deps
 * @returns guard
 */
export const createNeedsIDVerificationAlloy =
  (deps: NeedsIDVerificationAlloyDeps) =>
  (
    _: ApplicationGuardArgs,
    params:
      | CreateApplicationResult
      | ReCreateApplicationResult
      | ApplicationCheckoutResult
      | ApplicationPrepareCheckoutResult
  ): boolean => {
    const app = params.applicationRes.result;
    if (!app) {
      return false;
    }
    return deps.enableIDVerification && needsIDVerificationAlloy(app);
  };

type IsVirtualCardApplicationDeps = {
  isVirtualCard: boolean;
};

/**
 * Factory for `isVirtualCardApplication`. Returns the captured virtual-card
 * flag.
 * @param deps
 * @returns guard
 */
export const createIsVirtualCardApplication =
  (deps: IsVirtualCardApplicationDeps) => (): boolean => {
    return deps.isVirtualCard;
  };
